import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card } from '../../design-system';
import { resolveTenant, TenantPublicInfo } from '../../services/consentApi';
import { isPortalSessionValid } from '../../services/portalApi';
import { useI18n } from '../../i18n';
import '../../design-system/styles.css';

/**
 * Data Owner Help Page - Privacy Rights Information
 * Route: /t/:tenantSlug/my-consent/help
 */
const DataOwnerHelp: React.FC = () => {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const navigate = useNavigate();
  const { t, language, setLanguage, supportedLanguages } = useI18n();

  // State
  const [tenant, setTenant] = useState<TenantPublicInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedSection, setExpandedSection] = useState<string | null>('rights');

  const isLoggedIn = isPortalSessionValid();

  // Load tenant info
  useEffect(() => {
    const loadTenant = async () => {
      if (!tenantSlug) {
        setLoading(false);
        return;
      }

      try {
        const tenantInfo = await resolveTenant(tenantSlug);
        setTenant(tenantInfo);
      } catch {
        // Tenant not found - still show help page
      } finally {
        setLoading(false);
      }
    };

    loadTenant();
  }, [tenantSlug]);

  // FAQ sections
  const faqSections = [
    {
      id: 'rights',
      icon: '⚖️',
      title: t.portal?.yourRights || 'Your Privacy Rights',
      content: [
        {
          q: t.portal?.rightAccessQ || 'What is the right to access?',
          a: t.portal?.rightAccessA || 'You have the right to know what personal data is being collected about you, how it is being used, and who it is being shared with. You can request a copy of your data at any time.',
        },
        {
          q: t.portal?.rightCorrectQ || 'What is the right to correction?',
          a: t.portal?.rightCorrectA || 'If your personal data is inaccurate or incomplete, you can request that it be corrected or updated.',
        },
        {
          q: t.portal?.rightWithdrawQ || 'What is the right to withdraw consent?',
          a: t.portal?.rightWithdrawA || 'You can withdraw your consent at any time. Once withdrawn, the organization must stop processing your data for the purposes you consented to. Some data may be retained for legal compliance.',
        },
        {
          q: t.portal?.rightEraseQ || 'What is the right to erasure?',
          a: t.portal?.rightEraseA || 'Also known as the "right to be forgotten", you can request that your personal data be deleted. However, there may be legal reasons why the organization needs to retain some information.',
        },
      ],
    },
    {
      id: 'withdrawal',
      icon: '🔄',
      title: t.portal?.withdrawalFaq || 'Withdrawal Process',
      content: [
        {
          q: t.portal?.withdrawHowQ || 'How do I withdraw my consent?',
          a: t.portal?.withdrawHowA || 'Log in to your account, go to your consent history, and click "Withdraw Consent" on any active consent. You may optionally provide a reason for withdrawal.',
        },
        {
          q: t.portal?.withdrawTimeQ || 'How long does withdrawal take?',
          a: t.portal?.withdrawTimeA || 'Processing times depend on applicable laws: DPDP Act (India) - 7 days, GDPR (EU) - 30 days, CCPA (California) - 45 days. You will be notified when the process is complete.',
        },
        {
          q: t.portal?.withdrawEffectQ || 'What happens after I withdraw consent?',
          a: t.portal?.withdrawEffectA || 'The organization will stop using your data for the stated purposes. Some data may be retained for legal compliance. You may lose access to certain services that required your consent.',
        },
      ],
    },
    {
      id: 'security',
      icon: '🔒',
      title: t.portal?.securityFaq || 'Account Security',
      content: [
        {
          q: t.portal?.loginSecureQ || 'How is my login secured?',
          a: t.portal?.loginSecureA || 'We use a 3-step verification process: OTP sent to your registered phone/email, followed by a security question. This ensures only you can access your consent artifacts.',
        },
        {
          q: t.portal?.dataProtectQ || 'How is my data protected?',
          a: t.portal?.dataProtectA || 'All data is encrypted in transit and at rest. We follow industry best practices and comply with DPDP Act, GDPR, and other applicable privacy regulations.',
        },
      ],
    },
    {
      id: 'contact',
      icon: '📞',
      title: t.portal?.contactFaq || 'Contact & Support',
      content: [
        {
          q: t.portal?.grievanceQ || 'How do I file a grievance?',
          a: t.portal?.grievanceA || 'You can contact the organization\'s Data Protection Officer (DPO) or Grievance Officer. Their contact details should be provided in the privacy policy you consented to.',
        },
        {
          q: t.portal?.regulatorQ || 'Who is the regulatory authority?',
          a: t.portal?.regulatorA || 'In India, the Data Protection Board handles privacy complaints. In the EU, you can contact your national Data Protection Authority. In California, the Attorney General handles CCPA complaints.',
        },
      ],
    },
  ];

  // Loading state
  if (loading) {
    return (
      <main className="ds-container" style={{ textAlign: 'center', paddingTop: 100 }}>
        <p className="ds-text-muted">{t.common.loading}</p>
      </main>
    );
  }

  return (
    <main style={{ minHeight: '100vh', background: 'var(--ds-bg-secondary)' }}>
      {/* Header */}
      <header style={{ 
        background: 'white', 
        borderBottom: '1px solid var(--ds-border)',
        padding: '12px 16px',
      }}>
        <div style={{ 
          maxWidth: 600, 
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {isLoggedIn && (
              <button
                onClick={() => navigate(`/t/${tenantSlug}/my-consent/history`)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 24,
                  cursor: 'pointer',
                  padding: 4,
                }}
              >
                ←
              </button>
            )}
            <div>
              <h1 style={{ fontSize: 18, margin: 0, fontWeight: 600 }}>
                {t.portal?.help || 'Help & Privacy Rights'}
              </h1>
              {tenant && (
                <p style={{ fontSize: 14, margin: 0, color: 'var(--ds-text-muted)' }}>
                  {tenant.name}
                </p>
              )}
            </div>
          </div>

          {/* Language selector */}
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value as any)}
            aria-label="Select language"
            style={{
              padding: '8px 10px',
              borderRadius: 6,
              border: '1px solid var(--ds-border)',
              fontSize: 14,
              background: 'white',
            }}
          >
            {supportedLanguages.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.nativeName}
              </option>
            ))}
          </select>
        </div>
      </header>

      <div style={{ maxWidth: 600, margin: '0 auto', padding: '16px' }}>
        {/* Intro card */}
        <Card style={{ marginBottom: 16, padding: 20, textAlign: 'center' }}>
          <h2 style={{ fontSize: 20, margin: '0 0 8px' }}>
            {t.portal?.helpTitle || 'Understanding Your Privacy Rights'}
          </h2>
          <p style={{ margin: 0, color: 'var(--ds-text-secondary)', lineHeight: 1.6 }}>
            {t.portal?.helpSubtitle || 'Learn about your rights under privacy laws and how to manage your consent.'}
          </p>
        </Card>

        {/* FAQ Sections */}
        {faqSections.map((section) => (
          <Card key={section.id} style={{ marginBottom: 12, overflow: 'hidden' }}>
            <button
              onClick={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
              style={{
                width: '100%',
                padding: 16,
                background: 'none',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 24 }}>{section.icon}</span>
                <span style={{ fontSize: 16, fontWeight: 600 }}>{section.title}</span>
              </span>
              <span style={{ fontSize: 20, color: 'var(--ds-text-muted)' }}>
                {expandedSection === section.id ? '−' : '+'}
              </span>
            </button>

            {expandedSection === section.id && (
              <div style={{ padding: '0 16px 16px' }}>
                {section.content.map((item, index) => (
                  <div 
                    key={index} 
                    style={{ 
                      padding: '12px 0',
                      borderTop: index > 0 ? '1px solid var(--ds-border)' : 'none',
                    }}
                  >
                    <h4 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600 }}>
                      {item.q}
                    </h4>
                    <p style={{ margin: 0, fontSize: 14, color: 'var(--ds-text-secondary)', lineHeight: 1.6 }}>
                      {item.a}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </Card>
        ))}

        {/* Quick actions */}
        <Card style={{ padding: 16, marginTop: 24 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>
            {t.portal?.quickActions || 'Quick Actions'}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {isLoggedIn ? (
              <>
                <Link 
                  to={`/t/${tenantSlug}/my-consent/history`}
                  style={{
                    padding: '14px 16px',
                    borderRadius: 8,
                    background: 'var(--ds-accent)',
                    color: 'white',
                    textDecoration: 'none',
                    textAlign: 'center',
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                >
                  📋 {t.portal?.viewMyConsents || 'View My Consents'}
                </Link>
                <Link 
                  to={`/t/${tenantSlug}/my-consent/withdrawals`}
                  style={{
                    padding: '14px 16px',
                    borderRadius: 8,
                    background: 'white',
                    border: '1px solid var(--ds-border)',
                    color: 'var(--ds-text)',
                    textDecoration: 'none',
                    textAlign: 'center',
                    fontSize: 14,
                  }}
                >
                  📝 {t.portal?.viewWithdrawalRequests || 'View Withdrawal Requests'}
                </Link>
              </>
            ) : (
              <Link 
                to={`/t/${tenantSlug}/my-consent/login`}
                style={{
                  padding: '14px 16px',
                  borderRadius: 8,
                  background: 'var(--ds-accent)',
                  color: 'white',
                  textDecoration: 'none',
                  textAlign: 'center',
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                🔐 {t.portal?.loginToViewConsents || 'Login to View Your Consents'}
              </Link>
            )}
          </div>
        </Card>

        {/* Privacy law reference */}
        <div style={{ 
          marginTop: 24, 
          padding: 16, 
          background: 'white',
          borderRadius: 8,
          fontSize: 14,
          color: 'var(--ds-text-muted)',
          lineHeight: 1.6,
        }}>
          <strong>{t.portal?.applicableLaws || 'Applicable Privacy Laws'}:</strong>
          <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
            <li>Digital Personal Data Protection Act, 2023 (India)</li>
            <li>General Data Protection Regulation - GDPR (EU)</li>
            <li>California Consumer Privacy Act - CCPA (USA)</li>
          </ul>
        </div>
      </div>
    </main>
  );
};

export default DataOwnerHelp;
